import { adminBffFetch } from '@avila-roffe/api-clients-admin-bff';
import { getBffUrl } from '../../lib/env';
import type {
  AttachmentMeta,
  CreateSkillReleaseDto,
  Skill,
  SkillFile,
  SkillRelease,
  SkillReleaseFlat,
} from './types.js';

const API = '/api/v1';

function toRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}

function extractRows(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;

  const payloadRecord = toRecord(payload);
  const directData = payloadRecord.data;
  if (Array.isArray(directData)) return directData;

  const nestedData = toRecord(directData).data;
  if (Array.isArray(nestedData)) return nestedData;

  return [];
}

function extractOne(payload: unknown): unknown {
  const payloadRecord = toRecord(payload);
  const directData = payloadRecord.data;
  if (directData !== undefined) {
    const nestedData = toRecord(directData).data;
    return nestedData ?? directData;
  }
  return payload;
}

function normalizeSkill(raw: unknown): Skill {
  const row = toRecord(raw);
  const iface = toRecord(row.interface);

  return {
    id: String(row.id ?? ''),
    appSlug: String(row.appSlug ?? ''),
    name: String(row.name ?? ''),
    tenantSlug: typeof row.tenantSlug === 'string' ? row.tenantSlug : null,
    scope: (['PUBLIC', 'APPLICATION', 'TENANT'].includes(row.scope as string)
      ? row.scope
      : 'APPLICATION') as Skill['scope'],
    status: (['ACTIVE', 'DRAFT', 'DEPRECATED'].includes(row.status as string)
      ? row.status
      : 'DRAFT') as Skill['status'],
    version: typeof row.version === 'string' ? row.version : '1.0.0',
    content: String(row.content ?? ''),
    interface: {
      displayName: String(iface.displayName ?? ''),
      shortDescription:
        typeof iface.shortDescription === 'string' ? iface.shortDescription : undefined,
      promptSuggestions: Array.isArray(iface.promptSuggestions)
        ? (iface.promptSuggestions as string[])
        : undefined,
    },
    toolRefs: Array.isArray(row.toolRefs) ? (row.toolRefs as string[]) : [],
    compatibleTools: Array.isArray(row.compatibleTools) ? (row.compatibleTools as string[]) : [],
    tags: Array.isArray(row.tags) ? (row.tags as string[]) : [],
    gitPath: typeof row.gitPath === 'string' ? row.gitPath : null,
    gitSha: typeof row.gitSha === 'string' ? row.gitSha : null,
    createdBy: typeof row.createdBy === 'string' ? row.createdBy : null,
    updatedBy: typeof row.updatedBy === 'string' ? row.updatedBy : null,
    createdAt: String(row.createdAt ?? ''),
    updatedAt: String(row.updatedAt ?? ''),
  };
}

function normalizeSkillFile(raw: unknown): SkillFile {
  const row = toRecord(raw);
  return {
    id: String(row.id ?? ''),
    path: String(row.path ?? ''),
    content: String(row.content ?? ''),
    createdAt: String(row.createdAt ?? ''),
    updatedAt: String(row.updatedAt ?? ''),
  };
}

export async function listSkillsForApp(
  appSlug: string,
  { status = '', limit = 100 }: { status?: string; limit?: number } = {},
): Promise<Skill[]> {
  const query = new URLSearchParams({ appSlug, limit: String(limit) });
  if (status) query.set('status', status);
  const response = await adminBffFetch<unknown>(`${API}/skills?${query}`, { method: 'GET' });
  return extractRows(toRecord(response).data).map(normalizeSkill);
}

export async function getSkill(appSlug: string, name: string): Promise<Skill | null> {
  const response = await adminBffFetch<unknown>(`${API}/skills/${appSlug}/${name}`, {
    method: 'GET',
  });

  const item = extractOne(toRecord(response).data);
  if (Object.keys(toRecord(item)).length === 0) return null;

  return normalizeSkill(item);
}

export async function createSkill(data: {
  name: string;
  appSlug: string;
  content?: string;
  status?: string;
  scope?: string;
  tenantSlug?: string | null;
  tags?: string[];
  compatibleTools?: string[];
  interface?: { displayName?: string; shortDescription?: string };
}): Promise<Skill> {
  const response = await adminBffFetch<unknown>(`${API}/skills`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return normalizeSkill(extractOne(toRecord(response).data));
}

export async function updateSkillMetadata(
  appSlug: string,
  name: string,
  payload: {
    name?: string;
    status?: Skill['status'];
    scope?: Skill['scope'];
    appSlug?: string;
    tenantSlug?: string | null;
    tags?: string[];
    compatibleTools?: string[];
    toolRefs?: string[];
    /** interface sub-fields */
    interface?: { displayName?: string; shortDescription?: string; promptSuggestions?: string[] };
  },
): Promise<Skill> {
  const response = await adminBffFetch<unknown>(`${API}/skills/${appSlug}/${name}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  return normalizeSkill(extractOne(toRecord(response).data));
}

/**
 * Elevates a skill's scope to APPLICATION.
 * This is a one-way (irreversible) operation — SUPER_ADMIN only on the UI.
 */
export async function elevateSkillScope(appSlug: string, name: string): Promise<Skill> {
  const response = await adminBffFetch<unknown>(`${API}/skills/${appSlug}/${name}`, {
    method: 'PATCH',
    body: JSON.stringify({ scope: 'APPLICATION' }),
  });
  return normalizeSkill(extractOne(toRecord(response).data));
}

export async function deleteSkill(appSlug: string, name: string): Promise<void> {
  await adminBffFetch<unknown>(`${API}/skills/${appSlug}/${name}`, { method: 'DELETE' });
}

export async function getSkillFiles(appSlug: string, name: string): Promise<SkillFile[]> {
  const response = await adminBffFetch<unknown>(`${API}/skills/${appSlug}/${name}/files`, {
    method: 'GET',
  });
  return extractRows(toRecord(response).data).map(normalizeSkillFile);
}

export async function upsertSkillFile(
  appSlug: string,
  name: string,
  path: string,
  content: string,
): Promise<SkillFile> {
  const response = await adminBffFetch<unknown>(`${API}/skills/${appSlug}/${name}/files`, {
    method: 'PUT',
    body: JSON.stringify({ path, content }),
  });
  return normalizeSkillFile(extractOne(toRecord(response).data));
}

export async function deleteSkillFile(appSlug: string, name: string, path: string): Promise<void> {
  const query = new URLSearchParams({ path });
  await adminBffFetch<unknown>(`${API}/skills/${appSlug}/${name}/files?${query.toString()}`, {
    method: 'DELETE',
  });
}

export async function exportSkill(
  appSlug: string,
  name: string,
  basePath?: string,
): Promise<{ exportedFiles: string[]; basePath: string }> {
  const response = await adminBffFetch<unknown>(`${API}/skills/${appSlug}/${name}/export`, {
    method: 'POST',
    body: JSON.stringify(basePath ? { basePath } : {}),
  });

  const data = toRecord(extractOne(toRecord(response).data));
  return {
    exportedFiles: Array.isArray(data.exportedFiles) ? (data.exportedFiles as string[]) : [],
    basePath: String(data.basePath ?? ''),
  };
}

export type InvokeResult = {
  text?: string;
  blocks?: import('@avila-roffe/components-web').ContentBlock[];
};

export async function invokeSkill(
  appSlug: string,
  name: string,
  prompt: string,
): Promise<InvokeResult> {
  const response = await adminBffFetch<unknown>(`${API}/skills/${appSlug}/${name}/invoke`, {
    method: 'POST',
    body: JSON.stringify({ prompt }),
  });
  const data = toRecord(extractOne(toRecord(response).data));
  const blocks = Array.isArray(data.blocks) ? data.blocks : undefined;
  const text = typeof data.response === 'string' ? data.response : undefined;
  return { text, blocks: blocks as InvokeResult['blocks'] };
}

export async function listAttachments(
  resourceType: 'skill' | 'workflow' | 'agent',
  resourceId: string,
): Promise<AttachmentMeta[]> {
  const response = await adminBffFetch<unknown>(
    `${API}/attachments/${resourceType}/${resourceId}`,
    {
      method: 'GET',
    },
  );

  const rows = extractRows(toRecord(response).data);
  return rows.map((raw) => {
    const row = toRecord(raw);
    return {
      id: String(row.id ?? ''),
      resourceType: row.resourceType as AttachmentMeta['resourceType'],
      resourceId: String(row.resourceId ?? ''),
      fileName: String(row.fileName ?? ''),
      mimeType: String(row.mimeType ?? 'application/octet-stream'),
      sizeBytes: typeof row.sizeBytes === 'number' ? row.sizeBytes : 0,
      uploadedBy: typeof row.uploadedBy === 'string' ? row.uploadedBy : null,
      createdAt: String(row.createdAt ?? ''),
    };
  });
}

export async function uploadAttachment(
  resourceType: 'skill' | 'workflow' | 'agent',
  resourceId: string,
  file: File,
): Promise<AttachmentMeta> {
  const bffUrl = getBffUrl();

  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${bffUrl}/api/v1/attachments/${resourceType}/${resourceId}`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });

  if (!response.ok) {
    throw new Error(`Upload failed: ${response.status}`);
  }

  const payload = (await response.json()) as { data?: unknown };
  return toRecord(payload.data ?? payload) as unknown as AttachmentMeta;
}

export function downloadAttachmentUrl(id: string): string {
  const bffUrl = getBffUrl();
  return `${bffUrl}/api/v1/attachments/download/${id}`;
}

export async function deleteAttachment(id: string): Promise<void> {
  const bffUrl = getBffUrl();

  const response = await fetch(`${bffUrl}/api/v1/attachments/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });

  if (!response.ok && response.status !== 204) {
    throw new Error(`Delete failed: ${response.status}`);
  }
}

// ─── Skill Releases ───────────────────────────────────────────────────────────

function normalizeRelease(raw: unknown): SkillRelease {
  const r = toRecord(raw);
  return {
    id: String(r.id ?? ''),
    skillId: String(r.skillId ?? ''),
    name: String(r.name ?? ''),
    status: (r.status as SkillRelease['status']) ?? 'DRAFT',
    notes: typeof r.notes === 'string' ? r.notes : null,
    snapshot:
      typeof r.snapshot === 'object' && r.snapshot !== null
        ? (r.snapshot as SkillRelease['snapshot'])
        : null,
    createdBy: typeof r.createdBy === 'string' ? r.createdBy : '',
    createdAt: String(r.createdAt ?? ''),
    updatedAt: String(r.updatedAt ?? ''),
  };
}

export async function listSkillReleases(appSlug: string, name: string): Promise<SkillRelease[]> {
  const response = await adminBffFetch<unknown>(`${API}/skills/${appSlug}/${name}/releases`);
  return extractRows(toRecord(response).data).map(normalizeRelease);
}

function normalizeReleaseFlat(raw: unknown): SkillReleaseFlat {
  const r = toRecord(raw);
  return {
    skillId: String(r.skillId ?? ''),
    appSlug: String(r.appSlug ?? ''),
    skillName: String(r.skillName ?? ''),
    skillDisplayName: String(r.skillDisplayName ?? ''),
    skillVersion: String(r.skillVersion ?? ''),
    skillScope: (r.skillScope as SkillReleaseFlat['skillScope']) ?? 'APPLICATION',
    skillStatus: (r.skillStatus as SkillReleaseFlat['skillStatus']) ?? 'ACTIVE',
    releaseId: String(r.releaseId ?? ''),
    releaseName: String(r.releaseName ?? ''),
    releaseStatus: (r.releaseStatus as SkillReleaseFlat['releaseStatus']) ?? 'DRAFT',
    notes: typeof r.notes === 'string' ? r.notes : null,
    snapshot:
      typeof r.snapshot === 'object' && r.snapshot !== null
        ? (r.snapshot as SkillReleaseFlat['snapshot'])
        : null,
    createdBy: String(r.createdBy ?? ''),
    createdAt: String(r.createdAt ?? ''),
    updatedAt: String(r.updatedAt ?? ''),
  };
}

/** Single-request alternative to N+1 per-skill fetches. */
export async function listAllSkillReleases(
  appSlug?: string,
  status?: string,
): Promise<SkillReleaseFlat[]> {
  const params = new URLSearchParams();
  if (appSlug && appSlug !== '_all') params.set('appSlug', appSlug);
  if (status) params.set('status', status);
  const qs = params.size > 0 ? `?${params}` : '';
  const response = await adminBffFetch<unknown>(`${API}/skills/releases${qs}`);
  return extractRows(toRecord(response).data).map(normalizeReleaseFlat);
}

export async function createSkillRelease(
  appSlug: string,
  name: string,
  dto: CreateSkillReleaseDto,
): Promise<SkillRelease> {
  const response = await adminBffFetch<unknown>(`${API}/skills/${appSlug}/${name}/releases`, {
    method: 'POST',
    body: JSON.stringify(dto),
  });
  return normalizeRelease(extractOne(toRecord(response).data));
}

export async function publishSkillRelease(
  appSlug: string,
  name: string,
  releaseId: string,
): Promise<SkillRelease> {
  const response = await adminBffFetch<unknown>(
    `${API}/skills/${appSlug}/${name}/releases/${releaseId}/publish`,
    { method: 'POST' },
  );
  return normalizeRelease(extractOne(toRecord(response).data));
}

export async function deprecateSkillRelease(
  appSlug: string,
  name: string,
  releaseId: string,
): Promise<SkillRelease> {
  const response = await adminBffFetch<unknown>(
    `${API}/skills/${appSlug}/${name}/releases/${releaseId}/deprecate`,
    { method: 'POST' },
  );
  return normalizeRelease(extractOne(toRecord(response).data));
}

export async function deleteSkillRelease(
  appSlug: string,
  name: string,
  releaseId: string,
): Promise<void> {
  await adminBffFetch<unknown>(`${API}/skills/${appSlug}/${name}/releases/${releaseId}`, {
    method: 'DELETE',
  });
}

export async function getSkillRelease(
  appSlug: string,
  name: string,
  releaseId: string,
): Promise<SkillRelease> {
  const response = await adminBffFetch<unknown>(
    `${API}/skills/${appSlug}/${name}/releases/${releaseId}`,
  );
  return normalizeRelease(extractOne(toRecord(response).data));
}

export async function updateSkillRelease(
  appSlug: string,
  name: string,
  releaseId: string,
  payload: { name?: string; notes?: string | null },
): Promise<SkillRelease> {
  const response = await adminBffFetch<unknown>(
    `${API}/skills/${appSlug}/${name}/releases/${releaseId}`,
    { method: 'PATCH', body: JSON.stringify(payload) },
  );
  return normalizeRelease(extractOne(toRecord(response).data));
}
